import { useCallback, useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Icon, NoSeBorra, ZonaPeligro } from "@/components/ds";
import { ArrowLeft, Loader2, Save } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import { VehiculoForm } from "@/pages/recursos/VehiculoForm";
import { RecursoDocumentosPanel } from "@/components/recurso-documentos/RecursoDocumentosPanel";
import type { Vehiculo } from "@/types/vehiculo";

/**
 * Ficha del vehículo — encabezado de identidad + tabs Información y Documentos.
 * Espejo estructural de `FichaTrabajador.tsx`, y por el mismo motivo: acá viven
 * las acciones que el listado dejó fuera (editar, activar/desactivar y mover a
 * la papelera), porque decidir sobre un vehículo sin ver antes su documentación
 * es justo lo que conviene evitar.
 *
 * A diferencia de la ficha del trabajador (que busca dentro del listado
 * completo por no existir un endpoint "por uuid"), acá SÍ existe
 * `GET /vehiculos/{id}` con route-model-binding numérico estándar — se usa
 * directo.
 */
export function FichaVehiculo() {
  const [, params] = useRoute("/vehiculos/:id/ficha");
  const [, navigate] = useLocation();
  const id = params?.id;

  const [vehiculo, setVehiculo] = useState<Vehiculo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [aEliminar, setAEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const cargar = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setNotFound(false);
    try {
      const res = await api.get<{ data: Vehiculo }>(`/vehiculos/${id}`);
      setVehiculo(res.data);
    } catch {
      // 404 (inexistente o de otra empresa) y cualquier otro error de carga
      // caen en el mismo estado "no encontrado" — sin fuga de información,
      // mismo criterio que `RecursoDocumentoContext::resolver()` (fc-api).
      setNotFound(true);
      setVehiculo(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Mover a la papelera vive SÓLO acá y en la fila del listado: desde la ficha
  // se decide con la documentación delante, que es el punto de tenerla.
  const moverAPapelera = async () => {
    if (!vehiculo) return;
    setEliminando(true);
    try {
      await api.delete(`/vehiculos/${vehiculo.id}`);
      toast.success(
        `Vehículo ${vehiculo.patente} movido a la papelera. Sus documentos se conservan.`,
      );
      navigate("/recursos/vehiculos");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Este vehículo ya está en la papelera.");
      } else {
        toast.error("No se pudo mover el vehículo a la papelera.");
      }
      setEliminando(false);
    }
  };

  if (!id) return null;

  const areasTexto =
    vehiculo && (vehiculo.areas ?? []).length > 0
      ? ordenarAreas(vehiculo.areas ?? [])
          .map(formatNombreArea)
          .join(" · ")
      : "Sin área";

  return (
    // `max-w-[900px]` sale del prototipo (`Vehículos.dc.html`, contenedor de la
    // ficha de equipo) y no es cosmético: sin tope, la fila de un documento
    // estira su primera columna (`minmax(180px,1fr)`) hasta ~1000px y deja el
    // nombre del documento en un extremo y su estado en el otro, con el medio
    // vacío. Una ficha es una superficie de lectura, no una tabla: la que se
    // estira a 1300px obliga a barrer la pantalla para reconstruir una sola
    // fila. Verificado rasterizando ambos anchos.
    <div className="max-w-[900px] space-y-5">
      <button
        type="button"
        onClick={() => navigate("/recursos/vehiculos")}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-[12.5px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Vehículos
      </button>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : notFound || !vehiculo ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            No se encontró el vehículo solicitado, o no pertenece a tu empresa.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Encabezado de identidad */}
          <div className="flex flex-wrap items-start gap-3.5">
            <span className="bg-primary-100 text-primary-hover flex size-[46px] shrink-0 items-center justify-center rounded-xl">
              <Icon name="local_shipping" size={24} />
            </span>
            <div className="min-w-[220px] flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="fc-num text-2xl font-bold tracking-[-.025em]">{vehiculo.patente}</h1>
                {vehiculo.eliminado_at ? (
                  <Badge variant="muted" dot>
                    En papelera
                  </Badge>
                ) : (
                  <Badge variant={vehiculo.activo ? "success" : "neutral"} dot>
                    {vehiculo.activo ? "Activo" : "Inactivo"}
                  </Badge>
                )}
              </div>
              <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-[12.5px]">
                <span>{[vehiculo.marca, vehiculo.modelo].filter(Boolean).join(" ") || "—"}</span>
                <span className="text-border-strong">·</span>
                <span>{vehiculo.tipo ?? "Sin tipo"}</span>
                {vehiculo.sigla && (
                  <>
                    <span className="text-border-strong">·</span>
                    <span className="fc-num">{vehiculo.sigla}</span>
                  </>
                )}
                <span className="text-border-strong">·</span>
                <span>{areasTexto}</span>
              </div>
            </div>
          </div>

          <Tabs defaultValue="info" variant="ficha" className="space-y-4">
            <TabsList className="max-w-full flex-wrap">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-3">
              {/* Sin Card envolvente: `layout="secciones"` ya trae sus propias
                  tarjetas, y anidarlas daría un borde dentro de otro borde. */}
              <VehiculoForm
                mode="edit"
                vehiculo={vehiculo}
                autoFocusFirst={false}
                layout="secciones"
                formClassName="space-y-3"
                onSaved={(actualizado) => setVehiculo(actualizado)}
                renderFooter={({ isSubmitting }) => (
                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSubmitting} className="gap-2">
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Guardar cambios
                    </Button>
                  </div>
                )}
              />

              <BloqueEnOperacion vehiculo={vehiculo} onCambiado={setVehiculo} />

              {/* La zona de peligro va fuera del <form> — ver ZonaPeligro. */}
              <ZonaPeligro
                titulo="Mover a la papelera"
                descripcion={
                  <>
                    Sale del inventario, pero <NoSeBorra />: sus documentos y los checklists
                    firmados se conservan. Puedes recuperarlo desde Vehículos › Papelera, o
                    eliminarlo definitivamente desde ahí.
                  </>
                }
                accion="Mover a la papelera"
                onAccion={() => setAEliminar(true)}
              />
            </TabsContent>

            <TabsContent value="documentos" className="space-y-4">
              <RecursoDocumentosPanel ambito="vehiculo" documentableId={vehiculo.id} />
            </TabsContent>
          </Tabs>

          <AlertDialog open={aEliminar} onOpenChange={(open) => !open && setAEliminar(false)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
                <AlertDialogDescription>
                  ¿Quieres mover el vehículo <strong>{vehiculo.patente}</strong> a la papelera? Sale
                  del inventario y de las asignaciones, pero puedes restaurarlo cuando quieras desde
                  la pestaña Papelera del listado.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    moverAPapelera();
                  }}
                  disabled={eliminando}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

/**
 * "En operación" — interruptor de efecto INMEDIATO, fuera del formulario.
 *
 * No es un campo más de la ficha: activar o desactivar un vehículo es una sola
 * decisión, y el prototipo la trata así. La alternativa —dejarlo dentro del
 * `<form>`— dejaba de funcionar cuando Guardar se movió adentro de la tarjeta
 * Identificación: el interruptor quedaba POR DEBAJO del botón que lo guardaba,
 * o sea que moverlo no hacía nada visible y el cambio se perdía al salir.
 *
 * Va por `PATCH /vehiculos/{id}/estado` y no por el `PUT` del formulario: el
 * endpoint sólo acepta `activo`, así que no puede arrastrar a la base las
 * ediciones a medio hacer que el usuario tenga en pantalla.
 */
function BloqueEnOperacion({
  vehiculo,
  onCambiado,
}: {
  vehiculo: Vehiculo;
  onCambiado: (vehiculo: Vehiculo) => void;
}) {
  const [guardando, setGuardando] = useState(false);

  const cambiar = async (activo: boolean) => {
    setGuardando(true);
    // Optimista: el interruptor tiene que responder al toque. Si la API falla
    // se revierte y se avisa — dejarlo "pensando" convierte una acción de un
    // click en una pantalla trabada.
    onCambiado({ ...vehiculo, activo });
    try {
      const res = await api.patch<{ data: Vehiculo }>(`/vehiculos/${vehiculo.id}/estado`, {
        activo,
      });
      onCambiado(res.data);
      toast.success(
        activo
          ? `${vehiculo.patente} quedó en operación.`
          : `${vehiculo.patente} quedó fuera de operación: sale de las asignaciones y de los checklists nuevos.`,
      );
    } catch {
      onCambiado(vehiculo);
      toast.error("No se pudo cambiar el estado del vehículo.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3.5">
      <div className="flex min-w-[220px] flex-1 flex-col gap-0.5">
        <Label htmlFor="vehiculo-en-operacion" className="text-[13px] font-semibold">
          {vehiculo.activo ? "En operación" : "Fuera de operación"}
        </Label>
        <p className="text-soft text-[11.5px] leading-[1.5]">
          {vehiculo.activo
            ? "Aparece en las asignaciones y en los checklists de terreno. Si lo desactivas sigue en el inventario con sus documentos."
            : "No aparece en asignaciones ni en checklists nuevos. Puedes reactivarlo cuando vuelva a operar."}
        </p>
      </div>
      <Switch
        id="vehiculo-en-operacion"
        checked={vehiculo.activo}
        onCheckedChange={cambiar}
        disabled={guardando}
      />
    </section>
  );
}
